import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Mail, Shield, LogOut, Calendar, Save, KeyRound } from "lucide-react";

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [busy, setBusy] = useState(false);

  const inp = "w-full h-11 px-4 rounded-xl bg-slate-950 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none";

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { name, email });
      setUser(data); toast.success("Profile updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };
  const changePassword = async () => {
    if (np.length < 6) return toast.error("New password must be 6+ characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: np });
      setCur(""); setNp(""); toast.success("Password changed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Change failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-6" data-testid="account-page">
      <h1 className="text-2xl font-head font-bold">Account</h1>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-slate-950 text-2xl font-bold overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U").toUpperCase()}
          </div>
          <div>
            <div className="text-xl font-head font-semibold">{user?.name}</div>
            <div className="text-sm text-slate-400">{user?.role === "admin" ? "Administrator" : "Member"}</div>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <Row icon={Mail} label="Email" value={user?.email} />
          <Row icon={Shield} label="Role" value={user?.role} />
          <Row icon={Shield} label="Sign-in method" value={user?.auth_provider === "google" ? "Google" : "Email / Password"} />
          <Row icon={Calendar} label="Member since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "-"} />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6" data-testid="edit-profile-card">
        <h2 className="font-head font-semibold mb-4">Edit profile</h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <label className="block"><span className="text-xs text-slate-400 mb-1 block">Name</span>
            <input data-testid="account-name" value={name} onChange={(e) => setName(e.target.value)} className={inp} /></label>
          <label className="block"><span className="text-xs text-slate-400 mb-1 block">Email</span>
            <input data-testid="account-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} /></label>
        </div>
        <button data-testid="save-profile" disabled={busy} onClick={saveProfile}
          className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50"><Save className="h-4 w-4" /> Save profile</button>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6" data-testid="change-password-card">
        <h2 className="font-head font-semibold mb-4 flex items-center gap-2"><KeyRound className="h-4 w-4 text-emerald-400" /> Change password</h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <label className="block"><span className="text-xs text-slate-400 mb-1 block">Current password</span>
            <input data-testid="current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder={user?.auth_provider === "google" && !user?.has_password ? "(not set — leave blank)" : ""} className={inp} /></label>
          <label className="block"><span className="text-xs text-slate-400 mb-1 block">New password</span>
            <input data-testid="new-password" type="password" value={np} onChange={(e) => setNp(e.target.value)} className={inp} /></label>
        </div>
        <button data-testid="change-password" disabled={busy} onClick={changePassword}
          className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-50"><KeyRound className="h-4 w-4" /> Update password</button>
      </div>

      {user?.role === "admin" && (
        <button data-testid="account-admin-link" onClick={() => navigate("/admin")}
          className="w-full h-11 rounded-xl border border-amber-500/30 text-amber-300 flex items-center justify-center gap-2 hover:bg-amber-500/10 transition-colors">
          <Shield className="h-4 w-4" /> Open Admin Panel
        </button>
      )}

      <button data-testid="account-logout" onClick={async () => { await logout(); navigate("/"); }}
        className="w-full h-11 rounded-xl border border-slate-700 flex items-center justify-center gap-2 hover:text-rose-400 hover:border-rose-500/40 transition-colors">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-800/60 last:border-0">
      <span className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /> {label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
